import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { NAV_LINKS } from "@/data/siteContent";
import { track } from "@/lib/api";

export default function Header() {
  const site = useSite();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-navy text-cream" data-testid="announcement-bar">
        <div className="container-site flex items-center justify-between gap-4 py-2 text-sm">
          <p className="font-medium">{site.announcement}</p>
          <a
            href={`tel:${site.phone_href}`}
            onClick={() => track("phone_click", { location: "announcement" })}
            className="hidden sm:inline-flex items-center gap-1.5 font-semibold text-gold-light hover:text-white transition-colors"
            data-testid="announcement-phone-link"
          >
            <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {site.phone_display}
          </a>
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur-md border-b border-navy-100 shadow-sm">
        <div className="container-site flex items-center justify-between gap-4 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="1907 Insurance Group home">
            <img src="/assets/logo.png" alt="1907 Insurance Group logo" className="h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-[15px] font-semibold transition-colors min-h-[44px] inline-flex items-center ${
                    isActive ? "text-navy bg-navy-50" : "text-mutedink hover:text-navy hover:bg-navy-50/60"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${site.phone_href}`}
              onClick={() => track("phone_click", { location: "header" })}
              className="inline-flex items-center gap-2 text-navy font-semibold text-[15px] min-h-[44px]"
              data-testid="header-phone-link"
            >
              <Phone className="h-4 w-4 text-gold-dark" aria-hidden="true" />
              <span className="leading-tight text-left">
                <span className="block text-xs font-semibold text-mutedink">Speak With Michael</span>
                {site.phone_display}
              </span>
            </a>
            <Link to="/quote" className="btn-accent !px-6 !py-2.5 !text-base" data-testid="header-quote-button"
              onClick={() => track("cta_click", { location: "header", action: "get_quote" })}>
              Get My Quote
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center min-h-[48px] min-w-[48px] rounded-md text-navy hover:bg-navy-50 transition-colors"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-button"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-navy-100 bg-white" aria-label="Mobile navigation" data-testid="mobile-nav">
            <div className="container-site py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-lg font-semibold min-h-[48px] flex items-center transition-colors ${
                      isActive ? "text-navy bg-navy-50" : "text-mutedink hover:text-navy hover:bg-navy-50/60"
                    }`
                  }
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3 grid grid-cols-1 gap-3">
                <Link to="/quote" className="btn-accent w-full" data-testid="mobile-quote-button">Get My Quote</Link>
                <a href={`tel:${site.phone_href}`} className="btn-primary w-full" data-testid="mobile-call-button"
                  onClick={() => track("phone_click", { location: "mobile_menu" })}>
                  <Phone className="h-5 w-5" aria-hidden="true" /> Speak With Michael — {site.phone_display}
                </a>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
